
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import ConfirmHoanThanh from "@/components/shared/confirm-hoan-thanh-modal/ConfirmHoanThanh.vue";
import TaiKhoanSelectionV2 from "@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue";
import { LOAI_DINH_MUC } from "@/core/constants/const";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import {
  getDuyetPhieuNhapXuatStatus,
  mapPhieuNhapXuatStatusToLabel,
  mapPhieuNhapXuatStatusToLevelColor,
  swalErrNotification,
  swalNotification,
  mapLoaiPhieuXuatToLabel,
} from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import usePhieuXuat from "@/core/hooks/usePhieuXuat";
import {
  ChenhLechVatTuBienBanResponse,
  IBienBanGiaiTheResponseBase,
  IVatTuDinhMucCoDinh,
  IVatTuNgoaiDinhMuc,
  IVatTuXuatKhoResponseBase,
  ReceiptBaseResponse,
} from "@/core/interfaces/ApiResponses";
import { BienBanGiaiTheService } from "@/core/services/BienBanGiaiThe.service";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import PageHeaderApprovals from "@/layout/header/PageHeaderApprovals.vue";
import { openPhieuXuatKhoPdf } from "@/components/shared/pdf-viewer/open-phieu-xuat-kho-pdf";
import PhieuXuatKhoVatTuTable from "@/components/phieu-xuat-kho/PhieuXuatKhoVatTuTable.vue";
import { buildPhieuXuatKhoSummaryGroups } from "@/components/phieu-xuat-kho/composables/usePhieuXuatKhoDetailSummary";
import type { IPhieuXuatKhoSelectedKhoSummary } from "@/components/phieu-xuat-kho/types";
import moment from "moment";
import { computed, defineComponent, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import BaseLoading from "@/components/shared/BaseLoading.vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import usePermission from "@/core/hooks/usePermission";
import { PHIEU_XUAT_KHO } from "@/core/constants/permissions";
import Can from "@/components/Can.vue";
import { mapMutations } from "vuex";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";
import {
  faCheckDouble,
  faCheckToSlot,
  faPencil,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCan as faTrash } from "@fortawesome/free-regular-svg-icons";

export default defineComponent({
  name: "phieu-xuat-kho-form",

  components: {
    BaseLink,
    BaseLoading,
    Can,
    DetailTopicLottie,
    ConfirmationDialog,
    ConfirmHoanThanh,
    LabelCustom,
    PageContentBase,
    PageContentSection,
    PageHeaderActionItem,
    PageHeaderActions,
    PageHeaderApprovals,
    PhieuXuatKhoVatTuTable,
    TaiKhoanSelectionV2,
  },

  setup() {
    const router = useRouter();
    const { goBack } = useRouterCustom();
    const idBienBan = ref(0);
    const { id: idPhieuXuat } = useRouterCustom("idPhieuXuatKho");
    const { startLoading, endLoading, isLoading } = useLoading();
    const { formatDisplayingDate } = useDateTime();

    const { allowEditingTaiKhoan, goToEditPhieuXuatLink, isPhieuXuatEditable } =
      usePhieuXuat();

    const openPhieuXuatKhoPdfViewer = () => {
      openPhieuXuatKhoPdf(router, phieuXuatDetail.value?.id);
    };
    const { canDuyetPxk } = usePermission();

    const isCreatingFormBierBan = ref(false);
    const vatTuSelected = ref<IVatTuXuatKhoResponseBase[]>([]);

    const phieuXuatDetail = ref<ReceiptBaseResponse>({
      count_not_in_dmd: 0,
      hoan_thanh: false,
      id: -1,
      id_bien_ban_giai_the: -1,
      id_bo_phan: -1,
      id_don_vi: -1, // thêm vào cho đủ, ko dùng đến
      id_kho: -1, // thêm vào cho đủ, ko dùng đến
      id_nguoi_dung: -1, // thêm vào cho đủ, ko dùng đến
      ten_nguoi_nhan: null,
      is_approved: false,
      so_bien_ban: "", // thêm vào cho đủ, ko dùng đến
      so_phieu: "", // thêm vào cho đủ, ko dùng đến
      created_at: "", // thêm vào cho đủ, ko dùng đến
      updated_at: "", // thêm vào cho đủ, ko dùng đến
      nam: 0, // thêm vào cho đủ, ko dùng đến
      bien_ban_giai_the: null,
      kho: null,
      ngay: null,
      vat_tu_xuat_kho: [],
      loai_phieu: 0, // thêm vào cho đủ, ko dùng đến
      loai_dinh_muc: 0, // thêm vào cho đủ, ko dùng đến
      ly_do: "",
      ngay_hoan_thanh: null,
      total_export_amount: 0, // thêm vào cho đủ, ko dùng đến
      tk_no: {
        ma_tk: "",
        ten_tk: "",
        ma_ct: "",
      },
    });

    const editable = computed(() => isPhieuXuatEditable(phieuXuatDetail.value));

    const editableTaiKhoan = computed(() =>
      allowEditingTaiKhoan(phieuXuatDetail.value)
    );

    const form = reactive({
      so_phieu: "",
      receiptReason: "",
      so_bien_ban: "",
      firstWarehouseId: -1,
      receiptDate: "",
    });

    const bienBanGiaiTheDetail = ref<IBienBanGiaiTheResponseBase | null>(null);

    const selectedKhoItems = ref<IPhieuXuatKhoSelectedKhoSummary[]>([]);

    const confirmingApproval = ref(false);
    const approvingPhieuXuat = ref<ReceiptBaseResponse | null>(null);

    const onConfirmApprovalDialog = () => {
      if (phieuXuatDetail.value) {
        approvingPhieuXuat.value = phieuXuatDetail.value;
        confirmingApproval.value = false;
      }
    };

    const confirmApproval = async () => {
      approvingPhieuXuat.value = null;
      await fetchPhieuXuatDetail(phieuXuatDetail.value.id);
    };

    const fetchPhieuXuatDetail = async (idPhieuXuat: number) => {
      const {
        data: { data: response },
      } = await PhieuXuatKhoService.get(idPhieuXuat);

      phieuXuatDetail.value = response;
    };

    return {
      PHIEU_XUAT_KHO,
      goBack,
      bienBanGiaiTheDetail,
      canDuyetPxk,
      editable,
      editableTaiKhoan,
      goToEditPhieuXuatLink,
      idBienBan,
      form,
      formatDisplayingDate,
      idPhieuXuat,
      isCreatingFormBierBan,
      openPhieuXuatKhoPdfViewer,
      fetchPhieuXuatDetail,
      vatTuSelected,
      selectedKhoItems,
      phieuXuatDetail,
      startLoading,
      endLoading,
      isLoading,
      confirmingApproval,
      approvingPhieuXuat,
      onConfirmApprovalDialog,
      confirmApproval,
      faCheckDouble,
      faCheckToSlot,
      faPencil,
      faPrint,
      faTrash,
    };
  },

  data() {
    return {
      isLoadingData: false,
      loaiDinhMuc: LOAI_DINH_MUC.TAT_CA_DINH_MUC as LOAI_DINH_MUC,
      showConfirmDeleteModal: false,
      vatTuDmcdItems: [] as IVatTuDinhMucCoDinh[],
    };
  },

  async mounted() {
    try {
      this.isLoadingData = true;
      await this.startLoading();

      await this.fetchPhieuXuatDetail(+this.idPhieuXuat);

      this.form.so_phieu = this.phieuXuatDetail.so_phieu;
      this.form.receiptReason = this.phieuXuatDetail.ly_do;

      if (this.phieuXuatDetail.id_bien_ban_giai_the) {
        this.isCreatingFormBierBan = true;
        this.idBienBan = this.phieuXuatDetail.id_bien_ban_giai_the;
      }

      if (this.isCreatingFormBierBan) {
        await this.fetchBienBanGiaiThe();
      }

      await this.fetchVatTuDinhMucCoDinh();

      const { loai_dinh_muc: loaiDinhMucResponse } = this.phieuXuatDetail;

      this.loaiDinhMuc =
        loaiDinhMucResponse === 1
          ? LOAI_DINH_MUC.DINH_MUC_CO_DINH
          : loaiDinhMucResponse === 2
          ? LOAI_DINH_MUC.NGOAI_DINH_MUC
          : LOAI_DINH_MUC.TAT_CA_DINH_MUC;

      await this.syncVatTuSelected();

      this.form.so_bien_ban = this.phieuXuatDetail?.so_bien_ban;
      this.form.receiptReason = this.phieuXuatDetail?.ly_do;

      this.form.receiptDate = moment(this.phieuXuatDetail.created_at).format(
        "DD-MM-YYYY"
      );

      this.form.firstWarehouseId = this.phieuXuatDetail.kho?.id
        ? this.phieuXuatDetail.kho.id
        : -1;

      this.selectedKhoItems = await buildPhieuXuatKhoSummaryGroups(
        this.phieuXuatDetail,
        this.vatTuSelected
      );

      setCurrentPageTitle(
        `PXK ${this.phieuXuatDetail.so_phieu} (${this.phieuXuatDetail.kho?.ten_kho})`
      );

      setBreadcrumbLinks([
        {
          text: "Phiếu xuất kho",
        },
      ]);

      document.title = `${this.phieuXuatDetail.so_phieu} - Phiếu xuất kho - ${this.phieuXuatDetail.kho?.ten_kho} - Đường sắt Hà Nội`;

      this.isLoadingData = false;
      await this.endLoading();
    } catch (e) {
      console.log("error: ", e);

      this.isLoadingData = false;
      await this.endLoading();

      await swalErrNotification(
        e,
        "Có lỗi khi lấy chi tiết thông tin phiếu xuất"
      );
    }
  },

  computed: {
    isPhieuXuatKhongTheoBienBan() {
      return +this.idBienBan === 0;
    },

    titleComputed(): string {
      return "Thông tin";
    },

    hasApprovalActions() {
      return Boolean(this.phieuXuatDetail?.processing_status);
    },
  },

  methods: {
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),
    mapPhieuNhapXuatStatusToLabel,
    mapPhieuNhapXuatStatusToLevelColor,
    getDuyetPhieuNhapXuatStatus,
    mapLoaiPhieuXuatToLabel,
    async fetchBienBanGiaiThe(): Promise<void> {
      if (this.isPhieuXuatKhongTheoBienBan) {
        return;
      }

      try {
        const {
          data: { data: bienBanData },
        } = await BienBanGiaiTheService.get(this.idBienBan);

        this.bienBanGiaiTheDetail = bienBanData;
      } catch (e) {
        console.log("error: ", e);

        this.isLoadingData = false;

        this.endLoading();

        await swalErrNotification(
          e,
          "Có lỗi khi lấy thông tin chi tiết của BBGT"
        );
      }
    },

    async fetchVatTuDinhMucCoDinh() {
      if (!this.bienBanGiaiTheDetail) return;

      try {
        const dinhMucResponse =
          await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
            id_cap_sc:
              this.bienBanGiaiTheDetail?.ke_hoach_sua_chua?.cap_sua_chua?.id ||
              this.bienBanGiaiTheDetail?.cap_sua_chua.id,
            id_dm_toa_xe:
              this.bienBanGiaiTheDetail?.id_danh_muc_toa_xe ||
              this.bienBanGiaiTheDetail?.ke_hoach_sua_chua
                ?.id_danh_muc_toa_xe ||
              this.bienBanGiaiTheDetail?.toa_xe.danh_muc_toa_xe[0].id,
          });

        if (dinhMucResponse) {
          const {
            data: {
              data: { vat_tu: vatTuItemsResponse },
            },
          } = await DinhMucCoDinhService.get(dinhMucResponse.id);

          if (vatTuItemsResponse) {
            this.vatTuDmcdItems = vatTuItemsResponse.map((item) => ({
              ...item,
              pivot: {
                id_dinh_muc: dinhMucResponse.id,
                id_vat_tu: item.id,
                so_luong: item.pivot.so_luong,
              },
            }));
          }
        }
      } catch (e) {
        console.log("error: ", e);

        this.endLoading();

        this.isLoadingData = false;

        await swalErrNotification(
          e,
          "Có lỗi xảy ra khi lấy thông tin vật tư của ĐMCĐ"
        );
      }
    },

    async syncVatTuSelected() {
      let vatTuChenhLechArr: ChenhLechVatTuBienBanResponse[] = [];

      if (this.loaiDinhMuc === LOAI_DINH_MUC.NGOAI_DINH_MUC) {
        const {
          data: { data: vatTuChenhLechResponse },
        } = await PhieuXuatKhoService.vaildateVatTuXuatKho({
          id: this.idBienBan,
        });

        vatTuChenhLechArr = vatTuChenhLechResponse;
      }

      this.vatTuSelected = this.vatTuItemsTransform({
        vatTuDmcdItems: this.vatTuDmcdItems,
        vatTuNgoaiDmItems: this.bienBanGiaiTheDetail?.ngoai_dinh_muc
          ? this.bienBanGiaiTheDetail.ngoai_dinh_muc
          : [],
        vatTuNdmChenhLechItems: vatTuChenhLechArr,
      });
    },

    vatTuItemsTransform({
      vatTuDmcdItems,
      vatTuNgoaiDmItems,
      vatTuNdmChenhLechItems,
    }: {
      vatTuDmcdItems: IVatTuDinhMucCoDinh[];
      vatTuNgoaiDmItems: IVatTuNgoaiDinhMuc[];
      vatTuNdmChenhLechItems: ChenhLechVatTuBienBanResponse[];
    }): IVatTuXuatKhoResponseBase[] {
      let result = [] as IVatTuXuatKhoResponseBase[];

      switch (this.loaiDinhMuc) {
        case LOAI_DINH_MUC.DINH_MUC_CO_DINH:
          result = vatTuDmcdItems.map((vatTu) => ({
            ...vatTu,
            pivot: {
              id_kho: 0,
              id_vat_tu: vatTu.id,
              so_luong: vatTu.pivot.so_luong,
              id_phieu_xuat_kho: 0,
            },
          }));

          break;
        case LOAI_DINH_MUC.NGOAI_DINH_MUC:
          result = vatTuNgoaiDmItems
            .filter((vatTu) => {
              const vatTuChenhLech = vatTuNdmChenhLechItems.find(
                ({ id_vat_tu: idVatTuChenhLech }) =>
                  idVatTuChenhLech === vatTu.id
              );

              if (!vatTuChenhLech) return false;

              return (
                vatTu.pivot.is_approved === 1 && +vatTuChenhLech.chenh_lech > 0
              );
            })
            .map((vatTu) => {
              const vatTuChenhLech = vatTuNdmChenhLechItems.find(
                ({ id_vat_tu: idVatTuChenhLech }) =>
                  idVatTuChenhLech === vatTu.id
              );

              return {
                ...vatTu,
                pivot: {
                  id_kho: 0,
                  id_vat_tu: vatTu.id,
                  so_luong: vatTuChenhLech?.chenh_lech ?? vatTu.pivot.so_luong,
                  id_phieu_xuat_kho: 0,
                },
              };
            });

          break;
        default:
          vatTuDmcdItems.forEach((vatTuInDmcd) => {
            let newQuantity = vatTuInDmcd.pivot.so_luong;

            for (let i = 0; i < vatTuNgoaiDmItems.length; i++) {
              if (
                vatTuNgoaiDmItems[i].id === vatTuInDmcd.id &&
                vatTuNgoaiDmItems[i].pivot.is_approved === 1
              ) {
                newQuantity += vatTuNgoaiDmItems[i].pivot.so_luong;
                break;
              }
            }

            result.push({
              ...vatTuInDmcd,
              pivot: {
                id_kho: 0,
                id_vat_tu: vatTuInDmcd.id,
                so_luong: newQuantity,
                id_phieu_xuat_kho: 0,
              },
            });
          });
      }

      return result;
    },

    async confirmDelete() {
      await PhieuXuatKhoService.delete(this.phieuXuatDetail.id);

      await swalNotification("Xoá phiếu xuất thành công");

      this.goBack();
    },
    async handleAdminSign() {
      if (!this.phieuXuatDetail) return;

      this.setDialog({
        title: `Phiếu xuất số ${this.phieuXuatDetail.so_phieu}`,
        message: "Xác nhận duyệt phiếu xuất này?",
        onConfirm: async () => {
          this.closeDialog();
          await this.startLoading();

          try {
            await PhieuXuatKhoService.adminSign(this.phieuXuatDetail.id);
            await this.endLoading();
            await swalNotification("Duyệt phiếu xuất thành công");
          } catch (e) {
            await this.endLoading();

            await swalErrNotification(e, "Có lỗi xảy ra");
          }
        },
      });
    },
  },
});
